/**
 * EscalationAnalyticsPanel — Super Admin: análisis de casos escalados por la IA.
 *
 * Muestra:
 *  - Total, distribución por categoría / prioridad / escuela
 *  - Turnos promedio antes del escalamiento
 *  - Tendencia últimos 30 días
 *  - Lista de últimos 20 escalamientos con la pregunta original
 *  - Botón "Generar clustering con IA" que agrupa las preguntas en temas
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  RefreshCw, Loader2, Sparkles, TrendingUp, AlertTriangle, Bot,
  Building2, MessagesSquare,
} from "lucide-react";

const CAT_COLORS = {
  bug: "bg-red-50 text-red-700 border-red-200",
  billing: "bg-amber-50 text-amber-700 border-amber-200",
  curriculum: "bg-indigo-50 text-indigo-700 border-indigo-200",
  support: "bg-blue-50 text-blue-700 border-blue-200",
  feature: "bg-purple-50 text-purple-700 border-purple-200",
  students: "bg-green-50 text-green-700 border-green-200",
  other: "bg-gray-50 text-gray-700 border-gray-200",
};

const PRIO_COLORS = {
  low: "bg-emerald-50 text-emerald-700",
  medium: "bg-amber-50 text-amber-700",
  high: "bg-orange-50 text-orange-700",
  urgent: "bg-red-50 text-red-700",
};

const StatCard = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
      </div>
    </div>
  </div>
);

export default function EscalationAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clustering, setClustering] = useState(false);
  const [clusterResult, setClusterResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/support-bot/escalations/analytics`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar analítica");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runCluster = async () => {
    setClustering(true);
    setClusterResult(null);
    try {
      const res = await axios.post(
        `${API}/support-bot/escalations/cluster`,
        { limit: 30 },
        { withCredentials: true, timeout: 60000 },
      );
      setClusterResult(res.data);
      toast.success(`Análisis IA completado (${res.data.count} preguntas)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el análisis");
    } finally {
      setClustering(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando análisis…
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <Bot className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="font-bold text-gray-700 mb-1">Aún no hay escalamientos IA</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          Cuando los Administradores Institucionales escalen un caso desde el
          chat flotante de soporte IA, aparecerán aquí para análisis.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={load}
          data-testid="escal-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
        </Button>
      </div>
    );
  }

  const catEntries = Object.entries(data.by_category || {}).sort((a, b) => b[1] - a[1]);
  const prioEntries = Object.entries(data.by_priority || {}).sort((a, b) => b[1] - a[1]);
  const trend = data.trend_last_30d || [];
  const maxTrend = Math.max(1, ...trend.map((t) => t.count));

  return (
    <div className="space-y-6" data-testid="escalation-analytics-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-5 h-5 text-purple-500" /> Análisis de escalamientos IA
          </h2>
          <p className="text-sm text-gray-500">
            Casos que la IA no pudo resolver y fueron enviados a soporte humano.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="escal-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1">Actualizar</span>
          </Button>
          <Button
            onClick={runCluster}
            disabled={clustering}
            className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white gap-1.5"
            size="sm"
            data-testid="escal-cluster-btn"
          >
            {clustering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generar clustering con IA
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={AlertTriangle} label="Total escalamientos" value={data.total} />
        <StatCard
          icon={MessagesSquare}
          label="Turnos promedio con IA"
          value={data.avg_turns_before_escalation}
          hint="antes de pedir humano"
        />
        <StatCard
          icon={TrendingUp}
          label="Últimos 30 días"
          value={trend.reduce((s, t) => s + t.count, 0)}
        />
        <StatCard
          icon={Building2}
          label="Escuelas distintas"
          value={(data.by_school || []).length}
        />
      </div>

      {/* Distribuciones */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <h3 className="font-bold text-sm text-gray-700 mb-3">Por categoría</h3>
          <div className="space-y-2">
            {catEntries.length === 0 && <p className="text-xs text-gray-400">Sin datos</p>}
            {catEntries.map(([cat, n]) => {
              const pct = Math.round((n / data.total) * 100);
              return (
                <div key={cat}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={`px-1.5 py-0.5 rounded-full border ${CAT_COLORS[cat] || CAT_COLORS.other}`}>{cat}</span>
                    <span className="text-gray-500">{n} ({pct}%)</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#419cca] to-[#8b5cf6]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <h3 className="font-bold text-sm text-gray-700 mb-3">Por prioridad</h3>
          <div className="space-y-2">
            {prioEntries.length === 0 && <p className="text-xs text-gray-400">Sin datos</p>}
            {prioEntries.map(([p, n]) => {
              const pct = Math.round((n / data.total) * 100);
              return (
                <div key={p}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={`px-1.5 py-0.5 rounded-full ${PRIO_COLORS[p] || PRIO_COLORS.medium}`}>{p}</span>
                    <span className="text-gray-500">{n} ({pct}%)</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <h3 className="font-bold text-sm text-gray-700 mb-3">Top escuelas escaladoras</h3>
          <div className="space-y-1.5 text-sm">
            {(data.by_school || []).slice(0, 6).map((s) => (
              <div key={s.school_id} className="flex items-center justify-between">
                <span className="text-gray-700 truncate flex-1">{s.school_name}</span>
                <span className="text-gray-500 text-xs">{s.count}</span>
              </div>
            ))}
            {(data.by_school || []).length === 0 && <p className="text-xs text-gray-400">Sin datos</p>}
          </div>
        </div>
      </div>

      {/* Trend chart */}
      {trend.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <h3 className="font-bold text-sm text-gray-700 mb-3">Tendencia últimos 30 días</h3>
          <div className="flex items-end gap-1 h-24">
            {trend.map((t) => (
              <div key={t.date} className="flex-1 flex flex-col items-center gap-1" title={`${t.date}: ${t.count}`}>
                <div
                  className="w-full bg-gradient-to-t from-[#419cca] to-[#8b5cf6] rounded-t"
                  style={{ height: `${(t.count / maxTrend) * 100}%`, minHeight: t.count ? "4px" : "0" }}
                />
                <span className="text-[9px] text-gray-400">{t.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Cluster result */}
      {clusterResult && (
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-100 rounded-2xl p-5" data-testid="cluster-result">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h3 className="font-bold text-gray-900">Análisis IA — patrones detectados</h3>
            <span className="ml-auto text-xs text-gray-500">
              {clusterResult.count} preguntas analizadas
            </span>
          </div>
          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {clusterResult.summary}
          </p>
        </div>
      )}

      {/* Recent escalations */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-bold text-sm text-gray-700">Últimos escalamientos</h3>
        </div>
        <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
          {(data.recent || []).map((r) => (
            <div key={r.id} className="p-4 hover:bg-gray-50 transition" data-testid={`escal-item-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 truncate">{r.subject}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {r.from_name}
                    {r.school_name && ` · ${r.school_name}`}
                    {" · "}
                    {new Date(r.created_at).toLocaleString("es")}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${CAT_COLORS[r.category] || CAT_COLORS.other}`}>
                    {r.category}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${PRIO_COLORS[r.priority] || PRIO_COLORS.medium}`}>
                    {r.priority}
                  </span>
                </div>
              </div>
              {r.first_question && (
                <div className="bg-gray-50 rounded-lg p-2.5 border-l-2 border-purple-300">
                  <p className="text-xs text-gray-600 italic line-clamp-3">&ldquo;{r.first_question}&rdquo;</p>
                  {r.turns_with_bot > 0 && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      {r.turns_with_bot} turno{r.turns_with_bot > 1 ? "s" : ""} con la IA antes de escalar
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
          {(data.recent || []).length === 0 && (
            <p className="text-center text-sm text-gray-400 p-6">Sin escalamientos recientes.</p>
          )}
        </div>
      </div>
    </div>
  );
}
